import { useState, useEffect, useRef } from 'react';

export function useCountUp(target, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  // Parse the target - handle formats like "+50", "+300K", "+22M", "+70%", "-40%", "24/7", "+68"
  const parseTarget = (val) => {
    const str = String(val);
    // Extract numeric part
    const match = str.match(/([+-]?)(\d+\.?\d*)/);
    if (!match) return { number: 0, prefix: '', suffix: '' };
    
    const sign = match[1] || '';
    const number = parseFloat(match[2]);
    const prefixIdx = str.indexOf(match[0]);
    const prefix = str.substring(0, prefixIdx) + sign;
    const suffix = str.substring(prefixIdx + match[0].length);
    
    return { number, prefix, suffix };
  };

  const { number: targetNumber, prefix, suffix } = parseTarget(target);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [hasStarted, startOnView]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime = null;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * targetNumber);
      
      setCount(current);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [hasStarted, targetNumber, duration]);

  const formattedCount = `${prefix}${count.toLocaleString()}${suffix}`;

  return { ref, formattedCount, hasStarted };
}
